import { Icon, InlayPanel } from '@sorrem-client/ui/components';
import { formatRecommendedReadiness, resolveVeilSaturationById } from '@sorrem/shared';
import classnames from 'classnames';
import type { CatalogCrucible } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { resolveCrucibleTraits } from '../../crucible-traits';
import { CrucibleTraitBadge } from '../CrucibleTraitBadge';
import styles from './CrucibleCard.module.scss';
import { toCrucibleAccentStyle } from '@sorrem-client/ui/features/CharacterHome/crucible-links';

interface CrucibleCardProps {
    crucible: CatalogCrucible;
    isSelected: boolean;
    onSelect: () => void;
    /** The character's own readiness, to read against the recommendation; null while unknown. */
    characterReadiness: number | null;
}

/** A crucible in the list: selecting it shows its details in the sidebar. */
export function CrucibleCard({ crucible, isSelected, onSelect, characterReadiness }: CrucibleCardProps): JSX.Element {
    const traits = resolveCrucibleTraits(crucible, characterReadiness);
    const isBelowRecommended = traits.some((trait) => trait.id === 'under-ready');

    return (
        <InlayPanel
            className={classnames(styles.crucibleCard, isSelected && styles.selected)}
            style={toCrucibleAccentStyle(crucible.accentColor)}
            aria-current={isSelected}
            onSelect={onSelect}
        >
            <div className={styles.crucibleInfo}>
                <p className={styles.crucibleName}>{crucible.name}</p>
                <p className={styles.crucibleTier}>{resolveVeilSaturationById(crucible.veilSaturation).displayName}</p>
                <p className={styles.recommendedReadiness}>
                    Recommended readiness
                    <Icon icon="crystal" size="small" className={styles.readinessIcon} />
                    <span className={styles.readinessValue}>{formatRecommendedReadiness(crucible.recommendedReadiness)}</span>
                    {characterReadiness !== null && (
                        <span className={classnames(styles.characterReadiness, isBelowRecommended && styles.belowRecommended)}>
                            {`- You: ${characterReadiness}`}
                        </span>
                    )}
                </p>
            </div>
            <div className={styles.crucibleTraits}>
                {traits.map((trait) => <CrucibleTraitBadge key={trait.id} trait={trait} />)}
            </div>
        </InlayPanel>
    );
}
